import React, { useState, useEffect } from 'react';
import { Users, Building2, MessageSquare, FileText, Star, DollarSign } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { adminApi } from '../services/adminApi';
import { StatsCard } from '../components/StatsCard';
import { COLORS } from '../../theme/colors';

// Rating buckets: good / neutral / bad. Status hues, not the categorical series.
const RATING_COLORS = [COLORS.success, COLORS.warning, COLORS.danger];

export function DashboardPage() {
  const [overview, setOverview] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [tokenUsage, setTokenUsage] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [overviewData, regData, tokenData, fbData] = await Promise.all([
        adminApi.getOverviewStats(),
        adminApi.getRegistrationStats(30),
        adminApi.getTokenUsageStats(30, 10),
        adminApi.getFeedbackStats()
      ]);
      setOverview(overviewData);
      setRegistrations(regData);
      setTokenUsage(tokenData);
      setFeedbackStats(fbData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>;
  }

  const feedbackPieData = feedbackStats ? [
    { name: 'Positive', value: feedbackStats.positive },
    { name: 'Neutral', value: feedbackStats.neutral },
    { name: 'Negative', value: feedbackStats.negative }
  ].filter(d => d.value > 0) : [];

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Dashboard</h1>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatsCard
          title="Total Users"
          value={overview?.total_users || 0}
          icon={Users}
          color={COLORS.chart1}
        />
        <StatsCard
          title="Workspaces"
          value={overview?.total_workspaces || 0}
          icon={Building2}
          color={COLORS.chart2}
        />
        <StatsCard
          title="Chats"
          value={overview?.total_chats || 0}
          icon={MessageSquare}
          color={COLORS.chart3}
        />
        <StatsCard
          title="Documents"
          value={overview?.total_documents || 0}
          icon={FileText}
          color={COLORS.chart4}
        />
        <StatsCard
          title="Feedbacks"
          value={overview?.total_feedbacks || 0}
          icon={Star}
          color={COLORS.chart5}
        />
        <StatsCard
          title="Total Spent"
          value={`$${(overview?.total_spent || 0).toFixed(2)}`}
          icon={DollarSign}
          color={COLORS.chart6}
        />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Registrations Chart */}
        <div className="chart-container">
          <div className="chart-title">User Registrations (Last 30 days)</div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={registrations}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke={COLORS.chart1} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Feedback Distribution */}
        <div className="chart-container">
          <div className="chart-title">Feedback Distribution</div>
          {feedbackPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={feedbackPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {feedbackPieData.map((entry, index) => (
                    <Cell key={index} fill={RATING_COLORS[index % RATING_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              No feedback data
            </div>
          )}
          {feedbackStats && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                Avg Rating: <strong>{feedbackStats.avg_rating}</strong> |
                Unresolved: <strong style={{ color: 'var(--danger)' }}>{feedbackStats.unresolved}</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Token Usage Chart */}
      <div className="chart-container">
        <div className="chart-title">Top Users by Token Usage (Last 30 days)</div>
        {tokenUsage.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tokenUsage} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="email" type="category" width={150} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => value.toLocaleString()} />
              <Bar dataKey="total_tokens" fill={COLORS.chart2} name="Tokens" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
            No token usage data
          </div>
        )}
      </div>
    </div>
  );
}
